import { useState } from "react";
import { auth, db } from "../../firebase"; // Path to src/firebase.js
import { createUserWithEmailAndPassword, updateProfile, sendEmailVerification, signOut } from "firebase/auth";
import { doc, setDoc, collection, query, where, getDocs, serverTimestamp } from "firebase/firestore";
import { Loader2, MailCheck } from "lucide-react";
import { Button } from "../ui/Button"; // Path to src/components/ui/Button
import FormInput from "../ui/FormInput";
import PasswordStrengthMeter from "../ui/PasswordStrengthMeter";
import { isPasswordValid, MIN_PASSWORD_LENGTH } from "@/utils/passwordStrength";

export default function RegisterForm({ onSuccess, onSwitchToLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    if (!isPasswordValid(password)) {
      return setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters and include a letter and a number.`);
    }
    if (password !== confirmPassword) return setError("Passwords do not match.");
    setLoading(true);
    try {
      // Nickname uniqueness check
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('username', '==', displayName.trim()));
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) throw new Error("Nickname taken.");

      const cred = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(cred.user, { displayName: displayName.trim() });
      await setDoc(doc(db, "users", cred.user.uid), {
        username: displayName.trim(),
        wins: 0,
        losses: 0,
        createdAt: serverTimestamp(),
        email: email,
        displayId: crypto.randomUUID(),
        // Initialize MMR stats
        mmr_normal_words: 0,
        mmr_normal_words_punc: 0,
        mmr_normal_words_nums: 0,
        mmr_normal_words_punc_nums: 0,
        mmr_normal_quotes: 0,
        mmr_marathon_words: 0,
        mmr_marathon_words_punc: 0,
        mmr_marathon_words_nums: 0,
        mmr_marathon_words_punc_nums: 0,
        mmr_marathon_quotes: 0,
      });

      // Send verification email and sign out
      await sendEmailVerification(cred.user);
      await signOut(auth);
      setVerificationSent(true);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (verificationSent) {
    return (
      <div className="flex flex-col gap-6 text-center animate-in fade-in zoom-in duration-300">
        <div className="flex justify-center">
          <div className="w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center text-emerald-400 border border-emerald-500/20">
            <MailCheck size={32} />
          </div>
        </div>
        <div>
          <h3 className="text-xl font-black text-white uppercase italic tracking-wider mb-2">Protocol Initiated</h3>
          <p className="text-zinc-400 text-xs font-mono leading-relaxed">
            A verification signal has been sent to <span className="text-white font-bold">{email}</span>.
            <br /><br />
            Please confirm your neural link frequency before accessing the mainframe.
          </p>
        </div>
        <Button onClick={onSwitchToLogin} size="lg" className="w-full">
          Return to Login
        </Button>
      </div>
    );
  }

  return (
    /* Matches the flex-col gap-6 from LoginForm */
    <div className="flex flex-col gap-6">
      <div className="text-center group">
        <h2 className="text-3xl font-black mb-1 text-white tracking-tighter uppercase italic bg-gradient-to-r from-indigo-400 via-purple-400 to-emerald-400 bg-clip-text text-transparent">
          Join the Battle
        </h2>
        <p className="text-[9px] text-zinc-500 uppercase tracking-[0.3em] font-black">New.Identity()</p>
      </div>

      {error && (
        <p className="bg-red-500/10 border border-red-500/20 text-red-400 text-xs text-center p-3 rounded-xl animate-pulse">
          {error}
        </p>
      )}

      <form onSubmit={handleRegister} className="flex flex-col gap-4">
        <FormInput label="Nickname" placeholder="Arena Nickname" value={displayName} onChange={(e) => setDisplayName(e.target.value)} required />
        <FormInput label="Email" type="email" placeholder="soldier@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required />
        
        <div className="grid grid-cols-2 gap-4">
          <FormInput
            label="Password"
            type="password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            minLength={MIN_PASSWORD_LENGTH}
            required
          />
          <FormInput
            label="Confirm"
            type="password"
            placeholder="••••••••"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
        </div>
        <PasswordStrengthMeter password={password} />

        <Button
          type="submit" 
          loading={loading}
          size="lg"
          className="w-full mt-2"
        >
          Forge Account
        </Button>
      </form>

      <div className="text-center pt-2">
        <p className="text-[10px] text-zinc-600 uppercase tracking-widest font-bold">
          Returning Combatant?{" "}
          <Button
            type="button" 
            onClick={onSwitchToLogin} 
            variant="link"
            className="ml-1"
          >
            Login Here
          </Button>
        </p>
      </div>
    </div>
  );
}